<template>
	<div :class="['table-container', ...variantClassList]">
		<header
			class="table-container__header"
			data-testid="table-container-header"
			v-if="showHeader"
		>
			<slot name="tableHeader">
				<!-- Optional Head Slot -->
			</slot>
		</header>
		<div
			:class="[
				'table-container__tables',
				isTotalRow && 'table__total-row'
			]"
			ref="tableContainer"
			:style="{
				'--header-height': `${headerHeight}px`,
				...(minHeight && { 'min-height': `${minHeight}px` })
			}"
			v-maxHeight="offset"
		>
			<table
				v-if="!isTabletMode"
				:class="['table is-desktop', responsive && 'is-responsive']"
				data-testid="table-desktop"
				ref="table"
			>
				<thead class="table__header" ref="tableHeader">
					<tr :class="['table__tr', rowAlignmentClass]">
						<th
							class="table__th table__th--checkbox"
							v-if="hasCheckbox"
						>
							<Checkbox
								@change="onSelectAll"
								:value="isCheckedSelectAll"
							/>
						</th>
						<th
							:class="[
								'table__th',
								column.columnModifierName !== undefined &&
									'table__th--' + column.columnModifierName,
								column.isSortable && 'is-sortable',
								column.alignDesktop !== undefined &&
									'is-align-' + column.alignDesktop,
								column.sortDirectionClass
							]"
							v-for="(column, index) in displayColumns"
							:key="column.columnName"
							tabindex="0"
							data-testid="table-desktop-col"
							:data-feature-switch-id="column.featureSwitchId"
							@click="
								handleSort(
									ascending,
									column.columnName,
									index,
									column.sortDirection,
									column.isSortable
								)
							"
						>
							<!-- NOTE FOR DEV: Use this when columns are sortable -->
							<div
								v-if="column.isSortable"
								class="table__sort-header"
							>
								<span class="table__header-label">
									{{ column.columnDisplayName }}
								</span>

								<div
									class="table__sort-icons"
									data-testid="table-desktop-sort"
								>
									<i
										:class="[
											'icon',
											column.sortDirectionIconClass
										]"
										data-testid="table-desktop-sort-icon"
									></i>
								</div>
							</div>

							<!-- NOTE FOR DEV: Use this when columns are NOT sortable -->
							<span v-else class="table__header-label">
								{{ column.columnDisplayName }}
							</span>
						</th>
					</tr>
				</thead>
				<tbody
					class="table__body"
					data-testid="table-desktop-body"
					ref="tableBody"
				>
					<tr
						:class="['table__tr', rowAlignmentClass]"
						v-for="(record, index) in displayRecordset"
						:key="record.id"
					>
						<td
							class="table__td table__td--checkbox"
							v-if="hasCheckbox"
						>
							<Checkbox
								v-model="recordset[index][selectionProp]"
								@change="
									value => {
										onSelect(value, index);
									}
								"
							/>
						</td>
						<td
							:class="[
								'table__td',
								'table__td--' + column.columnModifierName,
								column.alignDesktop !== undefined &&
									'is-align-' + column.alignDesktop
							]"
							v-for="column in displayColumns"
							:key="column.columnName"
							:data-feature-switch-id="column.featureSwitchId"
						>
							<slot
								:name="column.slot"
								v-bind:record="recordset[index]"
							>
								<component
									:is="
										column.component
											? column.component.name
											: 'span'
									"
									v-bind="
										column.component &&
										column.component.props
											? column.component.props(
													internalRecordset[index],
													column,
													dynamicComponentEventHandler
											  )
											: { class: 'table__data' }
									"
									v-on="
										column.component &&
										column.component.events
											? column.component.events(
													internalRecordset[index],
													column,
													dynamicComponentEventHandler
											  )
											: {}
									"
									>{{ record[column.columnName] }}</component
								>
							</slot>
						</td>
					</tr>
					<tr
						data-testid="table-desktop-body-noresults"
						v-if="showNoResults"
					>
						<td
							:style="{
								'min-height': `${minHeight - headerHeight}px`,
								padding: `${noResultsOffset}px`
							}"
							:colspan="displayColumns.length"
							data-testid="table-desktop-noresults-td"
						>
							<div ref="noResults">
								<slot name="noResults">
									<NoResultsMessage
										:visible="true"
										:primaryTextCondensed="
											noResultsPrimaryText
										"
										:secondaryTextCondensed="
											noResultsSecondaryText
										"
									/>
								</slot>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<!-- NOTE FOR DEV: the tablet viewport breakpoint is set at 1200 px -->
			<table
				v-else
				:class="['table is-tablet']"
				data-testid="table-tablet"
			>
				<tr class="table__tr" v-if="hasCheckbox">
					<th class="table__th">
						<Checkbox
							@change="onSelectAll"
							:value="isCheckedSelectAll"
						/>
					</th>
					<td
						class="table__td"
						v-for="(record, index) in displayRecordset"
						:key="record.id"
					>
						<Checkbox
							v-model="recordset[index][selectionProp]"
							@change="
								value => {
									onSelect(value, index);
								}
							"
						/>
					</td>
				</tr>
				<tr
					class="table__tr"
					:class="
						column.columnModifierName !== undefined &&
							'table__tr--' + column.columnModifierName
					"
					v-for="(column, index) in displayColumns"
					:key="column.columnName"
					:data-feature-switch-id="column.featureSwitchId"
				>
					<th
						:class="[
							'table__th',
							column.columnModifierName !== undefined &&
								'table__th--' + column.columnModifierName,
							column.isSortable && 'is-sortable',
							column.sortDirectionClass
						]"
						tabindex="0"
						data-testid="table-tablet-col"
						@click="
							handleSort(
								ascending,
								column.columnName,
								index,
								column.sortDirection,
								column.isSortable
							)
						"
					>
						<!-- NOTE FOR DEV: Use this when columns are sortable -->
						<div
							v-if="column.isSortable"
							class="table__sort-header"
						>
							<span class="table__header-label">
								{{ column.columnDisplayName }}
							</span>

							<div
								class="table__sort-icons"
								data-testid="table-tablet-sort"
							>
								<i
									:class="[
										'icon',
										column.sortDirectionIconClass
									]"
									data-testid="table-tablet-sort-icon"
								></i>
							</div>
						</div>

						<!-- NOTE FOR DEV: Use this when columns are NOT sortable -->
						<span v-else class="table__header-label">
							{{ column.columnDisplayName }}
						</span>
					</th>

					<td
						class="table__td"
						:class="[
							column.columnModifierName !== undefined &&
								'table__td--' + column.columnModifierName,
							column.alignTablet !== undefined &&
								'is-align-' + column.alignTablet
						]"
						v-for="(record, index) in displayRecordset"
						:key="record.id"
					>
						<slot
							:name="column.slot"
							v-bind:record="recordset[index]"
						>
							<component
								:is="
									column.component
										? column.component.name
										: 'span'
								"
								v-bind="
									column.component && column.component.props
										? column.component.props(
												internalRecordset[index],
												column,
												dynamicComponentEventHandler
										  )
										: {}
								"
								v-on="
									column.component && column.component.events
										? column.component.events(
												internalRecordset[index],
												column,
												dynamicComponentEventHandler
										  )
										: {}
								"
								>{{ record[column.columnName] }}</component
							>
						</slot>
					</td>
				</tr>
				<tr data-testid="table-tablet-noresults" v-if="showNoResults">
					<slot name="noResults">
						<NoResultsMessage
							:visible="true"
							:primaryTextCondensed="noResultsPrimaryText"
							:secondaryTextCondensed="noResultsSecondaryText"
						/>
					</slot>
				</tr>
			</table>

			<portal-target :name="portalTargetName || 'tableLoader'" />
		</div>
		<footer
			class="table-container__footer"
			v-if="isTotalRow && $slots['tableFooter']"
			data-testid="table-footer"
		>
			<slot name="tableFooter">
				<!-- Footer Content -->
			</slot>
		</footer>
	</div>
</template>

<script>
import variantClasses from './variant-classes/table-variant-classes';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import tableMixin from '@/mixins/table-mixin';
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox.vue';
import NumberDisplay from '@/components/visual/number-display/number-display.vue';
import Hyperlink from '@/components/typography/hyperlink/hyperlink.vue';
import NoResultsMessage from '@/components/shells/no-results-message/no-results-message.vue';

export default {
	name: 'Table',
	mixins: [componentVariantMixin, tableMixin],
	components: {
		Checkbox,
		NumberDisplay,
		Hyperlink,
		NoResultsMessage
	},
	data() {
		return {
			windowWidth: undefined,
			noResultsOffset: 0
		};
	},
	props: {
		responsive: {
			type: Boolean,
			default: true
		},
		isTotalRow: {
			type: Boolean,
			default: true
		},
		rowAlignment: {
			type: String,
			default: 'left',
			validator: val => {
				return ['left', 'center', 'right'].indexOf(val) >= 0;
			}
		},
		hasCheckbox: {
			type: Boolean,
			default: false
		},
		isCheckedSelectAll: {
			type: Boolean,
			default: false
		},
		maxHeightOverride: {
			type: Number,
			required: false
		},
		portalTargetName: {
			type: String,
			default: null
		}
	},
	created() {
		this.variantClasses = variantClasses;
	},
	mounted() {
		this.$emit('mounted', this.$refs);
		this.setNoResultsOffset();
	},
	computed: {
		rowAlignmentClass() {
			switch (this.rowAlignment) {
				case 'center':
					return 'is-align-center';
				case 'right':
					return 'is-align-right';
				case 'left':
				default:
					return '';
			}
		},
		showHeader() {
			return !!this.$slots.tableHeader;
		},
		selectionProp() {
			let propName = 'selected';

			if (this.hasCheckbox && this.columns) {
				const column = this.columns.find(
					record => record.isSelectionProp == true
				);

				if (column) {
					propName = column.columnName;
				}
			}

			return propName;
		},
		isTabletMode() {
			const result = this.windowWidth <= 1200 && this.responsive;
			let mode = 'desktop';

			if (result) {
				mode = 'tablet';
			}

			this.$emit('mode', mode);

			return result;
		}
	},
	methods: {
		onSelect(value, index) {
			this.$emit('onSelect', {
				item: this.recordset[index],
				checked: value.checked
			});
		},
		onSelectAll(value) {
			this.$emit('onSelectAll', value.checked);
		},
		setNoResultsOffset() {
			if (this.$refs?.noResults) {
				const { height } = this.$refs.noResults.getBoundingClientRect();
				this.noResultsOffset =
					(this.minHeight - this.headerHeight - height) / 2;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('table');
//thead header-height, the border isn't included in calc because it is an inset shadow
.table-container {
	display: block;
	position: relative;

	&__header {
		width: 100%;
		margin-bottom: $gutter-md;
	}

	&__tables {
		width: 100%;
		display: block;
		overflow: auto;
		border: 1px solid var(--neutral-2);
		border-radius: $border-radius;
	}

	&__footer {
		// Temporary Styles - UI/UX Pending
		width: 100%;
		border: $field-border-width solid var(--neutral-2);
		border-top: none;
		border-bottom-left-radius: $border-radius;
		border-bottom-right-radius: $border-radius;
		background: var(--bg-2);
		padding: $padding-sm;
	}
}

/******* Table Shared Styles *******/
.table {
	width: 100%;
	border-spacing: 0;
	overflow-y: auto;
	text-align: left;
	box-sizing: border-box;
	position: relative;
	font-size: $txt-font-size;
	background: var(--bg-0);
	color: var(--bg-0-fg-4);
	border-collapse: collapse;

	&__th {
		position: sticky;
		position: -webkit-sticky;
		top: 0;
		background: var(--bg-2);
		box-shadow: inset 0 -1px 0 var(--neutral-2); //cannot use border here because it will disappear with the sticky position
		z-index: $z-level1-1;

		.honda-flat-rate__results & {
			top: -1px;
		}
		&.is-sortable {
			white-space: nowrap;

			.table__sort-icons {
				display: inline-flex;
				margin-left: $gutter-sm;
				color: var(
					--neutral-3
				); /* is a known exception for deviating from the background/foreground combinations. */
			}

			&.is-ascending {
				.table__sort-icons {
					transform: rotateX(180deg);
				}
			}

			&:hover,
			&:focus {
				cursor: pointer;
				background-color: var(--brand-2-tint);

				.table__sort-icons {
					color: var(--bg-brand-2-tint-fg-2);
				}

				&.is-ascending,
				&.is-descending {
					.table__sort-icons {
						color: var(--bg-brand-2-tint-fg-2);
					}
				}
			}

			&:focus-visible {
				outline: none;
			}
		}
	}

	&__sort-header {
		display: inline-flex;
		align-items: center;
	}

	&__header {
		&-label {
			white-space: nowrap;
			font-weight: $bold-font-weight;
			color: var(--bg-2-fg-3);
		}
	}

	&__tr {
		& + .table__tr {
			border-top: 1px solid var(--neutral-2);
		}

		&:nth-child(even) {
			background: var(--bg-1);
			color: var(--bg-1-fg-3);

			&:hover {
				background-color: var(--bg-brand-2-tint);
				color: var(--bg-brand-2-tint-fg-2);
			}
		}

		&:nth-child(odd) {
			background: var(--bg-0);
			color: var(--bg-0-fg-4);

			&:hover {
				background-color: var(--bg-brand-2-tint);
				color: var(--bg-brand-2-tint-fg-2);
			}
		}
	}

	&__td,
	&__th {
		vertical-align: top;
		padding: $table-cell-padding-y $table-cell-padding-x;

		&.is-align-center {
			text-align: center;
		}

		&.is-align-right {
			text-align: right;
		}

		&.is-align-left {
			text-align: left;
		}
		&--checkbox
        {
            border-right: none;

            .table__td:nth-child(2) {
            border-left: none;
            }
        }

    }
    &:deep(.checkbox__body)
    {
        margin-left: 0;
    }

	&__td {
		&:not(:first-of-type) {
			border-left: 1px solid var(--neutral-2);
		}
	}

	&__data {
		word-break: break-all;
	}

	&__total-row {
		&::-webkit-scrollbar-track {
			border-bottom-right-radius: 0;
		}

		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}

	.is-align-right {
		.table__td {
			text-align: right;
		}
	}

	.is-align-center {
		.table__td {
			text-align: center;
		}
	}

}
/******* Tablet Only Styles *******/
.table.is-tablet {
	height: 100%;

	.table {
		&__th {
			left: 0;
			width: 130px;
			height: 100%;
			box-shadow: inset -1px 0 0 var(--neutral-2); //cannot use border here because it will disappear with the sticky position
		}

		&__sort-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
	}

	@include mq($from: lg) {
		display: none;
	}
}

/******* Desktop Only Styles *******/
.table.is-desktop.is-responsive {
	@include mq($until: lg) {
		display: none;
	}
}

/* Nested variant */

.table {
	&--nested {
		@include scrollbar('borderless-bg');
		.table-container {
			&__tables {
				border: none;
			}

			&__footer {
				border: none;
				border-top: none;
			}
		}
		.table {
			background: transparent;

			&__tr {
				& + .table__tr {
					border-top: none;
				}

				&:nth-child(even),
                &:nth-child(odd){
                    background: transparent;
                    color: var(--bg-2-fg-3); //defining since nested area in the row-collapsed table has bg-2.

                }//removing hover and focus state as nested table row will not have any.
			}

			&__th {
				background: var(--bg-0);
				box-shadow: none;

				&:hover,
				&:focus {
					background-color: var(--bg-0);
				}
			}

			&__header-label
            {
                color: var(--bg-0-fg-4);
            }

			&__td {
				padding: $table-nested-cell-padding-y $table-cell-padding-x;

				&:not(:first-of-type) {
					border-left: none;
				}
			}
		}
		.table.is-tablet {
			display: none;
		}

		.table.is-desktop {
			display: table;
		}
	}
}

/* LL Styles v1 Only */
.table {
	&__th {
		&.is-sortable {
			&.is-ascending,
			&.is-descending {
				.table__sort-icons {
					color: var(--bg-2-fg-3);
				}
			}
		}
	}
}

/* LL Styles v2 Only */
/* .table {
	&__th {
		&.is-sortable {
			&.is-ascending,
			&.is-descending {
				.table__sort-icons {
					color: var(--brand-1);
				}
			}
		}
	}
} */
</style>
